import React, { useLayoutEffect, useRef, useState } from 'react';
import { coverUrl } from '../utils/coverUrl';
import { coverChain } from '../utils/coverChain';
import './CoverArt.scss';

// THE shared <img> for remote cover art. Use it anywhere a list/card renders
// a cover from the catalog instead of a bare <img>:
//   - picks a right-sized CDN variant for `size` (the CSS px of the box at
//     THIS call site) × devicePixelRatio — see utils/coverUrl.js
//   - lazy-loads by default so below-the-fold rows don't starve the visible
//     ones on slow connections; pass `eager` for above-the-fold hero art
//   - genuinely slow arrivals fade in; cache-hits paint INSTANTLY (a fade on
//     every cache-hit made scrolling lists shimmer — rows mounted at opacity
//     0 and eased in even though the bytes were already local)
//   - falls back to the original URL if the rewritten variant errors
// Layout/shape stays with the caller's className — this only owns fetching.
const FAST_LOAD_MS = 120; // under this = cache/local, render with no fade
// Covers already shown this session. A lazy, async-decoded <img> is never
// complete on its first frame even from cache, so reopening a page (Likes,
// an artist) blanked every cover for a frame and popped them back. A seen
// URL renders eager + sync-decoded and visible from the first paint.
const seenUrls = new Set();

//   - `covers` (the catalogue's [{url, source}] list) is the fallback chain:
//     a dead url (a 404 avatar) moves to the next picture, then to `fallback`
//     (a node, e.g. the letter avatar) — never a broken image
const CoverArt = ({ src: rawSrc, covers, fallback = null, size, alt = '', className = '', eager = false, onLoad, ...rest }) => {
    // Single choke point for remote covers: a 1001tracklists asset renders nothing.
    const chain = coverChain(rawSrc, covers);
    const chainKey = chain.join('\n');
    // Position in the chain: each picture is tried as its sized variant, then raw.
    const [pos, setPos] = useState({ key: chainKey, i: 0, raw: false });
    const cur = pos.key === chainKey ? pos : { key: chainKey, i: 0, raw: false };
    const exhausted = cur.i >= chain.length;
    const src = exhausted ? null : chain[cur.i];
    const failed = cur.raw;
    // false | 'instant' | 'fade'
    const url = exhausted ? null : (failed ? src : coverUrl(src, size));
    const seen = !!url && seenUrls.has(url);
    const [loaded, setLoaded] = useState(() => (seen ? 'instant' : false));
    const mountedAtRef = useRef(null);
    if (mountedAtRef.current === null) mountedAtRef.current = performance.now();
    const imgRef = useRef(null);
    // A remount must never blank art the browser already has. State starts
    // hidden (opacity 0 until load), but a cached image can be complete
    // before React attaches the load handler — seen on the artist page,
    // where swapping the pending header for the full one made the
    // already-painted avatar vanish until a re-request finished.
    useLayoutEffect(() => {
        const img = imgRef.current;
        if (img && img.complete && img.naturalWidth > 0) setLoaded('instant');
    }, [url]);
    if (!url) return exhausted && chain.length ? fallback : (fallback ?? null);
    return (
        <img
            ref={imgRef}
            src={url}
            alt={alt}
            className={`${className} cover-art${loaded ? ` cover-art-loaded${loaded === 'instant' ? ' cover-art-instant' : ''}` : ''}`}
            loading={eager || seen ? undefined : 'lazy'}
            decoding={seen ? 'sync' : 'async'}
            fetchpriority={eager ? 'high' : undefined}
            onLoad={(e) => {
                seenUrls.add(url);
                // Already showing (seen / cached) stays as is: switching to 'fade' re-ran the fade.
                setLoaded((prev) => prev || (performance.now() - mountedAtRef.current < FAST_LOAD_MS ? 'instant' : 'fade'));
                if (onLoad) onLoad(e);
            }}
            onError={() => {
                // sized variant failed → the raw url; raw failed → the next picture
                if (!cur.raw && url !== src) setPos({ key: chainKey, i: cur.i, raw: true });
                else setPos({ key: chainKey, i: cur.i + 1, raw: false });
            }}
            {...rest}
        />
    );
};

export default CoverArt;
